import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ShoppingCart } from 'lucide-react';
import { useSettings } from '@/hooks/useProducts';
import { useCartStore } from '@/stores/cart-store';
import { useUIStore } from '@/stores/ui-store';
import { formatCurrency } from '@/lib/utils';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import BrandSymbol from '@/components/BrandSymbol';

export default function PostPurchaseUpsell({ orderId }: { orderId: string }) {
  const { data: settingsData } = useSettings();
  const config = settingsData?.settings?.upsell_config?.post_purchase_upsell;
  const addItem = useCartStore((s) => s.addItem);
  const addToast = useUIStore((s) => s.addToast);

  const { data } = useQuery({
    queryKey: ['post-purchase-upsell', orderId],
    queryFn: () => api.get<{ recommendations: any[] }>(`/orders/${orderId}/recommendations`),
    enabled: !!orderId && !!config?.enabled,
  });

  if (!config?.enabled) return null;

  const recommendations = data?.recommendations || [];
  if (recommendations.length === 0) return null;

  return (
    <section className="mt-8 border border-line rounded-xl p-6 bg-surface" aria-labelledby="post-purchase-heading">
      <h2 id="post-purchase-heading" className="text-lg font-bold text-ink mb-4">
        {config.title || 'Complete Your Order'}
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {recommendations.slice(0, config.max_recommendations || 4).map((product: any) => (
          <div key={product.id} className="text-center">
            <Link to={`/product/${product.slug}`} className="block">
              <div className="aspect-square rounded-lg overflow-hidden bg-surface-muted mb-2">
                {product.image_url ? (
                  <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <BrandSymbol className="h-8 w-8 text-ink-muted" />
                  </div>
                )}
              </div>
              <p className="text-sm font-medium text-ink truncate">{product.name}</p>
            </Link>
            <p className="text-sm font-bold text-sage-700 mb-2">{formatCurrency(Number(product.price))}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  if (product.variant_id) await addItem(product.variant_id);
                  addToast('Added to cart!', 'success');
                } catch {
                  addToast('Failed to add', 'error');
                }
              }}
            >
              <ShoppingCart className="h-3 w-3" aria-hidden="true" />
              Add
            </Button>
          </div>
        ))}
      </div>
    </section>
  );
}
